/**
 * ═══════════════════════════════════════════════════════════
 * DEVBRAINS — EMAIL NOTIFICATION SYSTEM
 * ═══════════════════════════════════════════════════════════
 *
 * 26 email templates across 6 categories:
 *   Auth · Booking · Payment · Certification · Mentor · Support
 *
 * Admin can:
 *   - Enable / disable any template
 *   - Edit subject line and HTML body
 *   - Configure SMTP (host, port, user, pass)
 *   - Set from-name, from-email, reply-to
 *   - Send test emails
 *   - View email send log (last 100)
 *   - Manage calendar invite (.ics) attachment
 *
 * TODO: Connect sendEmail() to POST /api/email/send
 * ═══════════════════════════════════════════════════════════
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ── Event IDs ────────────────────────────────────────────────
export const EMAIL_EVENTS = {
  REGISTER_WELCOME:         'register_welcome',
  EMAIL_VERIFICATION:       'email_verification',
  LOGIN_ALERT:              'login_alert',
  PASSWORD_RESET:           'password_reset',
  BOOKING_CONFIRMED_MENTEE: 'booking_confirmed_mentee',
  BOOKING_CONFIRMED_MENTOR: 'booking_confirmed_mentor',
  BOOKING_CANCELLED:        'booking_cancelled',
  BOOKING_RESCHEDULED:      'booking_rescheduled',
  BOOKING_REMINDER_24H:     'booking_reminder_24h',
  BOOKING_REMINDER_1H:      'booking_reminder_1h',
  SESSION_COMPLETED:        'session_completed',
  PAYMENT_SUCCESS:          'payment_success',
  PAYMENT_FAILED:           'payment_failed',
  REFUND_INITIATED:         'refund_initiated',
  CERT_ENROLLED:            'cert_enrolled',
  CERT_SUBMISSION_RECEIVED: 'cert_submission_received',
  CERT_FEEDBACK_GIVEN:      'cert_feedback_given',
  CERT_ISSUED:              'cert_issued',
  MENTOR_APPLICATION:       'mentor_application',
  MENTOR_APPROVED:          'mentor_approved',
  MENTOR_REJECTED:          'mentor_rejected',
  PAYOUT_PROCESSED:         'payout_processed',
  EVENT_REGISTERED:         'event_registered',
  EVENT_REMINDER:           'event_reminder',
  SUPPORT_TICKET_CREATED:   'support_ticket_created',
  SUPPORT_TICKET_RESOLVED:  'support_ticket_resolved',
};

// ── Shared email chrome (header + footer) ────────────────────
const H = (accentColor = '#1DB954', emoji = '', title = '') =>
`<div style="font-family:'Inter',Arial,sans-serif;max-width:600px;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.08)">
<div style="background:linear-gradient(135deg,${accentColor},${accentColor}dd);padding:36px 32px;text-align:center">
  <div style="display:inline-block;background:rgba(255,255,255,.18);border-radius:50%;width:56px;height:56px;line-height:56px;font-size:28px;margin-bottom:12px">${emoji}</div>
  <h1 style="color:#fff;font-size:22px;font-weight:800;margin:0;letter-spacing:-.3px">${title}</h1>
</div>
<div style="background:#fff;padding:32px">`;

const F = (platformUrl = 'www.devbrains.in', unsubUrl = '') =>
`</div>
<div style="background:#f9fafb;padding:20px 32px;text-align:center;border-top:1px solid #e5e7eb">
  <a href="https://${platformUrl}" style="color:#1DB954;font-weight:700;text-decoration:none;font-size:13px">${platformUrl}</a>
  <span style="color:#d1d5db;margin:0 8px">·</span>
  <a href="mailto:support@devbrains.in" style="color:#9ca3af;font-size:13px;text-decoration:none">support@devbrains.in</a>
  ${unsubUrl ? `<span style="color:#d1d5db;margin:0 8px">·</span><a href="${unsubUrl}" style="color:#9ca3af;font-size:12px;text-decoration:none">Unsubscribe</a>` : ''}
</div>
</div>`;

const btn = (url, label, color = '#1DB954') =>
`<a href="${url}" style="display:inline-block;background:${color};color:#fff;padding:13px 28px;border-radius:10px;text-decoration:none;font-weight:800;font-size:15px;margin:8px 0">${label}</a>`;

const infoRow = (label, value) =>
`<tr><td style="padding:9px 12px;font-weight:700;color:#374151;font-size:13px;background:#f9fafb;border-bottom:1px solid #f3f4f6;white-space:nowrap">${label}</td><td style="padding:9px 12px;color:#111827;font-size:13px;border-bottom:1px solid #f3f4f6">${value}</td></tr>`;

const table = (rows) =>
`<table style="width:100%;border-collapse:collapse;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;margin:20px 0">${rows}</table>`;

// ── Default templates ────────────────────────────────────────
export const DEFAULT_TEMPLATES = [

  // ── AUTH ────────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.REGISTER_WELCOME, category:'Auth', enabled:true,
    label:'👋 Welcome — New Registration',
    to:'{{email}}',
    subject:'🎉 Welcome to DevBrains, {{firstName}}! Your free session is waiting',
    html: H('#1DB954','🚀','Welcome to DevBrains!') +
`<p style="font-size:16px;color:#111827;margin:0 0 12px">Hi <strong>{{firstName}}</strong>,</p>
<p style="color:#374151;line-height:1.8;margin:0 0 20px">Your account is ready and your <strong>1 free introductory session</strong> credit has been added. No credit card needed.</p>
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:18px 20px;margin:0 0 24px">
  <p style="margin:0 0 4px;font-weight:800;color:#166534;font-size:14px">🎁 Your Free Session Credit</p>
  <p style="margin:0;color:#166534;font-size:13px">Book any introductory session with any mentor — completely free.</p>
</div>
<p style="text-align:center;margin:24px 0">${btn('{{dashboardUrl}}','Find Your Mentor →')}</p>
<p style="color:#9ca3af;font-size:13px;text-align:center">Questions? Reply to this email or visit our <a href="{{platformUrl}}/help" style="color:#1DB954">Help Center</a>.</p>` +
F('{{platformUrl}}','{{unsubscribeUrl}}'),
    variables:['firstName','email','dashboardUrl','platformUrl','unsubscribeUrl'],
    preview:{ firstName:'Rahul', email:'rahul@ex.com', dashboardUrl:'https://devbrains.in/dashboard/mentee', platformUrl:'www.devbrains.in', unsubscribeUrl:'https://devbrains.in/unsubscribe' },
  },

  {
    id: EMAIL_EVENTS.EMAIL_VERIFICATION, category:'Auth', enabled:true,
    label:'✅ Email Verification',
    to:'{{email}}',
    subject:'✅ Please verify your DevBrains email address',
    html: H('#2563eb','✉️','Verify Your Email') +
`<p style="color:#374151;line-height:1.8">Hi <strong>{{firstName}}</strong>, click the button below to verify your email. This link <strong>expires in 30 minutes</strong>.</p>
<p style="text-align:center;margin:28px 0">${btn('{{verifyUrl}}','Verify Email →','#2563eb')}</p>
<p style="color:#9ca3af;font-size:12px;word-break:break-all">Or copy: {{verifyUrl}}</p>
<p style="color:#ef4444;font-size:13px">Didn't sign up? Ignore this email — your account won't be created.</p>` +
F('{{platformUrl}}'),
    variables:['firstName','email','verifyUrl','platformUrl'],
    preview:{ firstName:'Rahul', email:'rahul@ex.com', verifyUrl:'https://devbrains.in/verify?token=abc123', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.PASSWORD_RESET, category:'Auth', enabled:true,
    label:'🔑 Password Reset',
    to:'{{email}}',
    subject:'🔑 Reset your DevBrains password (expires in 30 min)',
    html: H('#ef4444','🔑','Password Reset') +
`<p style="color:#374151;line-height:1.8">Hi <strong>{{firstName}}</strong>, we received a request to reset your password.</p>
<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:14px;margin:16px 0">
  <p style="margin:0;color:#991b1b;font-size:13px">⚠️ This link expires in <strong>30 minutes</strong>. If you didn't request this, your account is safe — just ignore this email.</p>
</div>
<p style="text-align:center;margin:28px 0">${btn('{{resetUrl}}','Reset My Password →','#ef4444')}</p>
<p style="color:#9ca3af;font-size:12px;word-break:break-all">Or copy: {{resetUrl}}</p>` +
F('{{platformUrl}}'),
    variables:['firstName','email','resetUrl','platformUrl'],
    preview:{ firstName:'Rahul', email:'rahul@ex.com', resetUrl:'https://devbrains.in/reset?token=xyz789', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.LOGIN_ALERT, category:'Auth', enabled:false,
    label:'🔐 New Login Alert (optional)',
    to:'{{email}}',
    subject:'🔐 New sign-in to your DevBrains account from {{device}}',
    html: H('#374151','🔐','New Login Detected') +
`<p style="color:#374151">Hi <strong>{{firstName}}</strong>, a new sign-in was detected on your account:</p>
${table(infoRow('🕐 Time', '{{loginTime}}')+infoRow('💻 Device','{{device}}')+infoRow('📍 Location','{{location}}')+infoRow('🔑 Method','{{method}}'))}
<p style="color:#374151;font-size:14px">Not you? <a href="{{secureUrl}}" style="color:#ef4444;font-weight:700">Secure your account immediately →</a></p>` +
F('{{platformUrl}}'),
    variables:['firstName','email','loginTime','device','location','method','secureUrl','platformUrl'],
    preview:{ firstName:'Rahul', email:'rahul@ex.com', loginTime:'Mar 15, 2026 at 3:45 PM IST', device:'Chrome on Windows', location:'Mumbai, India', method:'Email & Password', secureUrl:'https://devbrains.in/security', platformUrl:'www.devbrains.in' },
  },

  // ── BOOKING ─────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.BOOKING_CONFIRMED_MENTEE, category:'Booking', enabled:true,
    label:'📅 Booking Confirmed (to Mentee)',
    to:'{{menteeEmail}}',
    subject:'📅 Confirmed: Session with {{mentorName}} on {{sessionDate}} at {{sessionTime}}',
    html: H('#1DB954','🎉','Session Confirmed!') +
`<p style="color:#111827;font-size:16px">Hi <strong>{{menteeName}}</strong>,</p>
<p style="color:#374151;line-height:1.8;margin:0 0 20px">Your session is confirmed! Here are all the details:</p>
${table(
  infoRow('👤 Mentor','{{mentorName}}')+
  infoRow('📅 Date','{{sessionDate}}')+
  infoRow('⏰ Time','{{sessionTime}} IST')+
  infoRow('⏱ Duration','{{duration}} minutes')+
  infoRow('💬 Type','{{sessionType}}')+
  infoRow('🎥 Platform','{{platform}}')+
  infoRow('🔗 Join Link','<a href="{{meetLink}}" style="color:#1DB954;font-weight:700">Join Meeting →</a>')+
  infoRow('💰 Amount','{{amountPaid}}')
)}
<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:14px;margin:0 0 20px">
  <p style="margin:0;color:#92400e;font-size:13px">⏰ You'll receive reminder emails <strong>24 hours</strong> and <strong>1 hour</strong> before your session.</p>
</div>
<p style="text-align:center">${btn('{{dashboardUrl}}','View in Dashboard →')}</p>` +
F('{{platformUrl}}','{{unsubscribeUrl}}'),
    variables:['menteeName','menteeEmail','mentorName','sessionDate','sessionTime','duration','sessionType','platform','meetLink','amountPaid','dashboardUrl','platformUrl','unsubscribeUrl'],
    preview:{ menteeName:'Rahul Verma', menteeEmail:'rahul@ex.com', mentorName:'Amar Krishnan', sessionDate:'Tue, Mar 15 2026', sessionTime:'3:00 PM', duration:'60', sessionType:'1:1 Mentoring', platform:'Google Meet', meetLink:'https://meet.google.com/abc-def-ghi', amountPaid:'₹1,600', dashboardUrl:'https://devbrains.in/dashboard/mentee', platformUrl:'www.devbrains.in', unsubscribeUrl:'https://devbrains.in/unsubscribe' },
  },

  {
    id: EMAIL_EVENTS.BOOKING_CONFIRMED_MENTOR, category:'Booking', enabled:true,
    label:'📅 Booking Confirmed (to Mentor)',
    to:'{{mentorEmail}}',
    subject:'📅 New booking: {{menteeName}} on {{sessionDate}} at {{sessionTime}}',
    html: H('#16a34a','💼','New Session Booked!') +
`<p style="color:#111827;font-size:16px">Hi <strong>{{mentorName}}</strong>,</p>
<p style="color:#374151;line-height:1.8;margin:0 0 20px">A new session has been booked with you. <strong style="color:#16a34a">₹{{earningAmount}}</strong> will be credited to your wallet after the session is completed.</p>
${table(
  infoRow('👤 Mentee','{{menteeName}}')+
  infoRow('📅 Date','{{sessionDate}}')+
  infoRow('⏰ Time','{{sessionTime}} IST')+
  infoRow('⏱ Duration','{{duration}} min')+
  infoRow('💬 Type','{{sessionType}}')+
  infoRow('💰 Your Earnings','<strong style="color:#16a34a">₹{{earningAmount}}</strong> (after 15% platform fee)')
)}
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px;margin:0 0 20px">
  <p style="margin:0;color:#166534;font-size:13px">💡 Please add your meeting link in the dashboard before the session.</p>
</div>
<p style="text-align:center">${btn('{{dashboardUrl}}','View Session →','#16a34a')}</p>` +
F('{{platformUrl}}'),
    variables:['mentorName','mentorEmail','menteeName','sessionDate','sessionTime','duration','sessionType','earningAmount','dashboardUrl','platformUrl'],
    preview:{ mentorName:'Amar Krishnan', mentorEmail:'amar@ex.com', menteeName:'Rahul Verma', sessionDate:'Tue, Mar 15 2026', sessionTime:'3:00 PM', duration:'60', sessionType:'1:1 Mentoring', earningAmount:'1,360', dashboardUrl:'https://devbrains.in/dashboard/mentor', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.BOOKING_REMINDER_24H, category:'Booking', enabled:true,
    label:'⏰ Session Reminder — 24 Hours Before',
    to:'{{email}}',
    subject:'⏰ Reminder: Your session tomorrow at {{sessionTime}} with {{otherPartyName}}',
    html: H('#f59e0b','📅','Session Tomorrow!') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>, just a friendly reminder — your session is <strong>tomorrow</strong>!</p>
<div style="background:#fffbeb;border:2px solid #f59e0b;border-radius:12px;padding:20px;text-align:center;margin:20px 0">
  <div style="font-size:14px;color:#92400e;font-weight:700;text-transform:uppercase;letter-spacing:.06em">{{sessionDate}}</div>
  <div style="font-size:32px;font-weight:900;color:#b45309;margin:6px 0">{{sessionTime}} IST</div>
  <div style="color:#92400e;font-size:13px">with <strong>{{otherPartyName}}</strong> · {{duration}} min · {{sessionType}}</div>
</div>
<p style="text-align:center;margin:20px 0">
  ${btn('{{meetLink}}','🎥 Open Meeting Link','#f59e0b')}
</p>
<p style="color:#9ca3af;font-size:12px;text-align:center">You'll receive another reminder 1 hour before.</p>` +
F('{{platformUrl}}'),
    variables:['name','email','otherPartyName','sessionDate','sessionTime','duration','sessionType','meetLink','platformUrl'],
    preview:{ name:'Rahul Verma', email:'rahul@ex.com', otherPartyName:'Amar Krishnan', sessionDate:'Tuesday, Mar 15 2026', sessionTime:'3:00 PM', duration:'60', sessionType:'1:1 Mentoring', meetLink:'https://meet.google.com/abc-def-ghi', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.BOOKING_REMINDER_1H, category:'Booking', enabled:true,
    label:'🔔 Session Reminder — 1 Hour Before',
    to:'{{email}}',
    subject:'🔔 Starting in 1 hour: Your session with {{otherPartyName}} at {{sessionTime}}',
    html: H('#ef4444','🔔','Starting in 1 Hour!') +
`<p style="color:#374151;text-align:center">Hi <strong>{{name}}</strong>, your session starts very soon!</p>
<div style="text-align:center;margin:24px 0">
  <div style="font-size:42px;font-weight:900;color:#dc2626">{{sessionTime}}</div>
  <div style="color:#6b7280;font-size:14px;margin-top:4px">with {{otherPartyName}} · {{sessionType}}</div>
</div>
<p style="text-align:center;margin:20px 0">
  ${btn('{{meetLink}}','🎥 Join Now →','#dc2626')}
</p>
<p style="color:#9ca3af;font-size:12px;text-align:center">Click the button above to open your meeting link.</p>` +
F('{{platformUrl}}'),
    variables:['name','email','otherPartyName','sessionTime','sessionType','meetLink','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', otherPartyName:'Amar Krishnan', sessionTime:'3:00 PM', sessionType:'1:1 Mentoring', meetLink:'https://meet.google.com/abc-def-ghi', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.BOOKING_CANCELLED, category:'Booking', enabled:true,
    label:'❌ Booking Cancelled',
    to:'{{email}}',
    subject:'❌ Session cancelled — {{sessionDate}} with {{otherPartyName}}',
    html: H('#6b7280','❌','Session Cancelled') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Your session on <strong>{{sessionDate}}</strong> at <strong>{{sessionTime}}</strong> with <strong>{{otherPartyName}}</strong> has been cancelled.</p>
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0;color:#166534;font-weight:700;font-size:14px">💚 Refund Status</p>
  <p style="margin:4px 0 0;color:#166534;font-size:13px">{{refundAmount}} will be refunded to your original payment method within 3–5 business days.</p>
</div>
<p style="color:#374151;font-size:14px">Reason: {{cancellationReason}}</p>
<p style="text-align:center;margin:20px 0">${btn('{{mentorsUrl}}','Find Another Mentor →','#1DB954')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','otherPartyName','sessionDate','sessionTime','refundAmount','cancellationReason','mentorsUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', otherPartyName:'Amar Krishnan', sessionDate:'Mar 15, 2026', sessionTime:'3:00 PM', refundAmount:'₹1,600', cancellationReason:'Mentor requested cancellation', mentorsUrl:'https://devbrains.in/mentors', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.BOOKING_RESCHEDULED, category:'Booking', enabled:true,
    label:'🔄 Booking Rescheduled',
    to:'{{email}}',
    subject:'🔄 Session rescheduled — now on {{newDate}} at {{newTime}}',
    html: H('#8b5cf6','🔄','Session Rescheduled') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Your session with <strong>{{otherPartyName}}</strong> has been rescheduled:</p>
${table(
  infoRow('❌ Old Date','<s style="color:#9ca3af">{{oldDate}} at {{oldTime}}</s>')+
  infoRow('✅ New Date','<strong style="color:#16a34a">{{newDate}} at {{newTime}} IST</strong>')+
  infoRow('⏱ Duration','{{duration}} min')+
  infoRow('💬 Type','{{sessionType}}')
)}
<p style="text-align:center;margin:20px 0">${btn('{{dashboardUrl}}','View Updated Session →','#8b5cf6')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','otherPartyName','oldDate','oldTime','newDate','newTime','duration','sessionType','dashboardUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', otherPartyName:'Amar Krishnan', oldDate:'Mar 15, 2026', oldTime:'3:00 PM', newDate:'Mar 18, 2026', newTime:'4:00 PM', duration:'60', sessionType:'1:1 Mentoring', dashboardUrl:'https://devbrains.in/dashboard/mentee', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.SESSION_COMPLETED, category:'Booking', enabled:true,
    label:'⭐ Session Completed — Review Request',
    to:'{{menteeEmail}}',
    subject:'⭐ How was your session with {{mentorName}}? Leave a review',
    html: H('#f59e0b','⭐','How Was Your Session?') +
`<p style="color:#374151">Hi <strong>{{menteeName}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Your session with <strong>{{mentorName}}</strong> is complete. We'd love to hear your feedback — it helps other learners find great mentors!</p>
<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:16px;margin:20px 0;text-align:center">
  <div style="font-size:28px;margin-bottom:8px">{{mentorName}}</div>
  <div style="color:#b45309;font-size:13px">{{sessionDate}} · {{sessionType}}</div>
</div>
<p style="text-align:center;margin:20px 0">${btn('{{reviewUrl}}','⭐ Leave a Review','#f59e0b')}</p>
<p style="color:#9ca3af;font-size:12px;text-align:center">Takes less than 60 seconds. Your feedback is anonymous to other users.</p>` +
F('{{platformUrl}}'),
    variables:['menteeName','menteeEmail','mentorName','sessionDate','sessionType','reviewUrl','platformUrl'],
    preview:{ menteeName:'Rahul', menteeEmail:'rahul@ex.com', mentorName:'Amar Krishnan', sessionDate:'Mar 15, 2026', sessionType:'1:1 Mentoring', reviewUrl:'https://devbrains.in/review/s1', platformUrl:'www.devbrains.in' },
  },

  // ── PAYMENT ─────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.PAYMENT_SUCCESS, category:'Payment', enabled:true,
    label:'💳 Payment Receipt',
    to:'{{email}}',
    subject:'🧾 Receipt: ₹{{amount}} paid — DevBrains session with {{mentorName}}',
    html: H('#1DB954','💳','Payment Successful') +
`<div style="text-align:center;margin:0 0 24px">
  <div style="font-size:48px;font-weight:900;color:#1DB954">₹{{amount}}</div>
  <div style="color:#6b7280;font-size:13px;margin-top:4px">Transaction ID: <code style="background:#f3f4f6;padding:2px 7px;border-radius:4px">{{paymentId}}</code></div>
</div>
${table(
  infoRow('📅 Date','{{paymentDate}}')+
  infoRow('👤 Mentor','{{mentorName}}')+
  infoRow('💬 Session','{{sessionType}} on {{sessionDate}}')+
  infoRow('🎫 Booking ID','{{bookingId}}')+
  infoRow('💳 Method','{{paymentMethod}}')
)}
<p style="text-align:center;margin:20px 0">
  ${btn('{{invoiceUrl}}','Download Invoice (PDF)','#374151')}
</p>` +
F('{{platformUrl}}'),
    variables:['email','amount','paymentId','paymentDate','mentorName','sessionType','sessionDate','bookingId','paymentMethod','invoiceUrl','platformUrl'],
    preview:{ email:'rahul@ex.com', amount:'1,600', paymentId:'pay_Nj1234567890', paymentDate:'Mar 10, 2026 at 2:14 PM', mentorName:'Amar Krishnan', sessionType:'1:1 Mentoring', sessionDate:'Mar 15, 2026', bookingId:'BK-2026-0042', paymentMethod:'UPI (GPay)', invoiceUrl:'https://devbrains.in/invoice/pay_Nj', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.PAYMENT_FAILED, category:'Payment', enabled:true,
    label:'❌ Payment Failed',
    to:'{{email}}',
    subject:'❌ Payment failed for your DevBrains session booking',
    html: H('#ef4444','❌','Payment Failed') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Unfortunately your payment of <strong>₹{{amount}}</strong> could not be processed. Your booking has not been confirmed.</p>
<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0;color:#991b1b;font-size:13px">Reason: {{failureReason}}</p>
</div>
<p style="color:#374151;line-height:1.7">Your session slot has been held for <strong>30 minutes</strong>. Please retry your payment.</p>
<p style="text-align:center;margin:20px 0">${btn('{{retryUrl}}','Retry Payment →','#ef4444')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','amount','failureReason','retryUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', amount:'1,600', failureReason:'Insufficient balance', retryUrl:'https://devbrains.in/booking/retry', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.REFUND_INITIATED, category:'Payment', enabled:true,
    label:'💚 Refund Initiated',
    to:'{{email}}',
    subject:'💚 Refund of ₹{{amount}} initiated for cancelled session',
    html: H('#16a34a','💚','Refund Initiated') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">A refund of <strong style="color:#16a34a">₹{{amount}}</strong> has been initiated for your cancelled session with {{mentorName}}.</p>
${table(infoRow('💰 Refund Amount','₹{{amount}}')+infoRow('📅 Initiated On','{{refundDate}}')+infoRow('⏳ Expected By','3–5 business days')+infoRow('🏦 To','Original payment method'))}
<p style="color:#374151;font-size:13px">Refund ID: <code>{{refundId}}</code></p>` +
F('{{platformUrl}}'),
    variables:['name','email','amount','mentorName','refundDate','refundId','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', amount:'1,600', mentorName:'Amar Krishnan', refundDate:'Mar 12, 2026', refundId:'rfnd_Nj9876543210', platformUrl:'www.devbrains.in' },
  },

  // ── CERTIFICATION ────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.CERT_ENROLLED, category:'Certification', enabled:true,
    label:'📚 Enrolled in Certification Track',
    to:'{{email}}',
    subject:'📚 You\'re enrolled in {{trackName}} — let\'s build something great!',
    html: H('#7c3aed','📚','Enrollment Confirmed!') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">You're officially enrolled in <strong>{{trackName}}</strong>. Your mentor is <strong>{{mentorName}}</strong>.</p>
<div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0 0 8px;font-weight:800;color:#5b21b6;font-size:14px">🗺️ What's Next:</p>
  <p style="margin:3px 0;color:#5b21b6;font-size:13px">1. Review your project requirements in the dashboard</p>
  <p style="margin:3px 0;color:#5b21b6;font-size:13px">2. Submit your first project within 7 days</p>
  <p style="margin:3px 0;color:#5b21b6;font-size:13px">3. Get code review from {{mentorName}}</p>
  <p style="margin:3px 0;color:#5b21b6;font-size:13px">4. Complete technical interview to earn certificate</p>
</div>
<p style="text-align:center">${btn('{{dashboardUrl}}','Start My Program →','#7c3aed')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','trackName','mentorName','dashboardUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', trackName:'Full-Stack Web Development', mentorName:'Amar Krishnan', dashboardUrl:'https://devbrains.in/dashboard/mentee/certifications', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.CERT_FEEDBACK_GIVEN, category:'Certification', enabled:true,
    label:'💬 Project Feedback Received',
    to:'{{email}}',
    subject:'💬 {{mentorName}} reviewed your project — {{feedbackStatus}}',
    html: H('#0891b2','💬','Project Feedback Ready') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8"><strong>{{mentorName}}</strong> has reviewed your latest project submission for <strong>{{trackName}}</strong>.</p>
<div style="background:{{statusBg}};border:1px solid {{statusBorder}};border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0;font-weight:800;color:{{statusColor}}">{{feedbackStatus}}</p>
  <p style="margin:8px 0 0;color:{{statusColor}};font-size:13px">{{feedbackSummary}}</p>
</div>
<p style="text-align:center">${btn('{{submissionUrl}}','View Full Feedback →','#0891b2')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','mentorName','trackName','feedbackStatus','feedbackSummary','statusBg','statusBorder','statusColor','submissionUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', mentorName:'Amar Krishnan', trackName:'Full-Stack Web Dev', feedbackStatus:'✅ Approved — Great work!', feedbackSummary:'Your API design is clean and the authentication flow is well-implemented. Minor: add input validation on the register endpoint.', statusBg:'#f0fdf4', statusBorder:'#bbf7d0', statusColor:'#166534', submissionUrl:'https://devbrains.in/cert/submissions/1', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.CERT_ISSUED, category:'Certification', enabled:true,
    label:'🏆 Certificate Issued',
    to:'{{email}}',
    subject:'🏆 Congratulations {{name}}! Your DevBrains Certificate is ready',
    html: H('#f59e0b','🏆','Certificate Issued!') +
`<p style="color:#374151;text-align:center;font-size:16px">Congratulations, <strong>{{name}}</strong>!</p>
<p style="color:#374151;line-height:1.8;text-align:center">You've successfully completed <strong>{{trackName}}</strong>. Your verified certificate is ready to share with the world.</p>
<div style="background:#fffbeb;border:2px solid #f59e0b;border-radius:12px;padding:20px;margin:20px 0;text-align:center">
  <div style="font-size:18px;font-weight:800;color:#92400e">{{trackName}}</div>
  <div style="font-size:13px;color:#b45309;margin-top:6px">Verification ID: <code style="background:#fef3c7;padding:2px 8px;border-radius:4px">{{verificationId}}</code></div>
  <div style="color:#b45309;font-size:12px;margin-top:4px">Verify at: devbrains.in/verify</div>
</div>
<div style="text-align:center;margin:20px 0">
  ${btn('{{certUrl}}','View Certificate →','#f59e0b')}
  &nbsp;
  ${btn('{{linkedinUrl}}','Share on LinkedIn →','#0A66C2')}
</div>` +
F('{{platformUrl}}'),
    variables:['name','email','trackName','verificationId','certUrl','linkedinUrl','platformUrl'],
    preview:{ name:'Rahul Verma', email:'rahul@ex.com', trackName:'Full-Stack Web Development', verificationId:'DB-2026-FS-001234', certUrl:'https://devbrains.in/cert/DB-2026-FS-001234', linkedinUrl:'https://linkedin.com/sharing/', platformUrl:'www.devbrains.in' },
  },

  // ── MENTOR ───────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.MENTOR_APPLICATION, category:'Mentor', enabled:true,
    label:'📝 Mentor Application Received',
    to:'{{email}}',
    subject:'📝 Your DevBrains mentor application is received — under review',
    html: H('#374151','📝','Application Received') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Thank you for applying to become a DevBrains mentor! Our team will review your profile within <strong>24–48 hours</strong>.</p>
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0 0 6px;font-weight:700;color:#166534;font-size:14px">While you wait:</p>
  <p style="margin:3px 0;color:#166534;font-size:13px">✅ Complete your profile — add bio, skills, work experience</p>
  <p style="margin:3px 0;color:#166534;font-size:13px">✅ Set your availability and pricing</p>
  <p style="margin:3px 0;color:#166534;font-size:13px">✅ Add your LinkedIn, GitHub, portfolio links</p>
</div>
<p style="text-align:center">${btn('{{profileUrl}}','Complete My Profile →','#374151')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','profileUrl','platformUrl'],
    preview:{ name:'Amar Krishnan', email:'amar@ex.com', profileUrl:'https://devbrains.in/dashboard/mentor/profile', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.MENTOR_APPROVED, category:'Mentor', enabled:true,
    label:'✅ Mentor Application Approved',
    to:'{{email}}',
    subject:'🎉 Your DevBrains mentor profile is LIVE — start accepting sessions!',
    html: H('#1DB954','🎓','Welcome, Mentor!') +
`<p style="color:#374151;font-size:16px">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Your mentor profile is now <strong>live and accepting bookings</strong>! Learners can now find and book sessions with you.</p>
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:18px 20px;margin:20px 0">
  <p style="margin:0 0 8px;font-weight:800;color:#166534;font-size:14px">🚀 Quick Start Checklist:</p>
  <p style="margin:4px 0;color:#166534;font-size:13px">☐ Set your weekly availability slots</p>
  <p style="margin:4px 0;color:#166534;font-size:13px">☐ Upload a professional profile photo</p>
  <p style="margin:4px 0;color:#166634;font-size:13px">☐ Add your bank account for payouts</p>
  <p style="margin:4px 0;color:#166534;font-size:13px">☐ Share your DevBrains profile link</p>
</div>
<p style="text-align:center">${btn('{{dashboardUrl}}','Go to Mentor Dashboard →')}</p>
<p style="color:#9ca3af;font-size:12px;text-align:center">Your profile: <a href="{{profileUrl}}" style="color:#1DB954">{{profileUrl}}</a></p>` +
F('{{platformUrl}}'),
    variables:['name','email','dashboardUrl','profileUrl','platformUrl'],
    preview:{ name:'Amar Krishnan', email:'amar@ex.com', dashboardUrl:'https://devbrains.in/dashboard/mentor', profileUrl:'https://devbrains.in/mentors/amar-krishnan', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.MENTOR_REJECTED, category:'Mentor', enabled:true,
    label:'❌ Mentor Application Rejected',
    to:'{{email}}',
    subject:'DevBrains Mentor Application — Update on your application',
    html: H('#6b7280','📋','Application Update') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Thank you for your interest in becoming a DevBrains mentor. After reviewing your application, we're unable to approve it at this time.</p>
<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0 0 4px;font-weight:700;color:#374151;font-size:14px">Reason:</p>
  <p style="margin:0;color:#6b7280;font-size:13px">{{rejectionReason}}</p>
</div>
<p style="color:#374151;line-height:1.7;font-size:14px">You're welcome to reapply after 3 months. Please address the feedback above and strengthen your application.</p>
<p style="text-align:center">${btn('mailto:support@devbrains.in','Contact Support','#374151')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','rejectionReason','platformUrl'],
    preview:{ name:'John Smith', email:'john@ex.com', rejectionReason:'Insufficient professional experience — minimum 3 years required. Profile was missing portfolio links and work history.', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.PAYOUT_PROCESSED, category:'Mentor', enabled:true,
    label:'💸 Payout Processed',
    to:'{{email}}',
    subject:'💸 Your payout of ₹{{amount}} has been processed — {{payoutDate}}',
    html: H('#16a34a','💸','Payout Processed!') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<div style="text-align:center;margin:20px 0">
  <div style="font-size:48px;font-weight:900;color:#16a34a">₹{{amount}}</div>
  <div style="color:#6b7280;font-size:13px">transferred to your bank account ending in <strong>****{{last4}}</strong></div>
</div>
${table(infoRow('📅 Processed On','{{payoutDate}}')+infoRow('🏦 Bank','****{{last4}}')+infoRow('📋 Reference','{{payoutRef}}')+infoRow('⏳ Expected','2–3 business days'))}
<p style="text-align:center">${btn('{{dashboardUrl}}','View Wallet & History →','#16a34a')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','amount','last4','payoutDate','payoutRef','dashboardUrl','platformUrl'],
    preview:{ name:'Amar Krishnan', email:'amar@ex.com', amount:'12,240', last4:'5678', payoutDate:'Mar 12, 2026', payoutRef:'PO-2026-0031', dashboardUrl:'https://devbrains.in/dashboard/mentor/earnings', platformUrl:'www.devbrains.in' },
  },

  // ── EVENTS ───────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.EVENT_REGISTERED, category:'Events', enabled:true,
    label:'🎤 Event Registration Confirmed',
    to:'{{email}}',
    subject:'🎤 You\'re registered for {{eventTitle}} on {{eventDate}}',
    html: H('#8b5cf6','🎤','Registration Confirmed!') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">You're registered for <strong>{{eventTitle}}</strong>!</p>
${table(infoRow('📅 Date','{{eventDate}}')+infoRow('⏰ Time','{{eventTime}} IST')+infoRow('👤 Host','{{hostName}}')+infoRow('🎥 Platform','{{platform}}')+infoRow('🔗 Join Link','<a href="{{meetLink}}" style="color:#8b5cf6;font-weight:700">Join Event →</a>'))}
<p style="text-align:center;margin:20px 0">${btn('{{dashboardUrl}}','View in Dashboard →','#8b5cf6')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','eventTitle','eventDate','eventTime','hostName','platform','meetLink','dashboardUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', eventTitle:'React 19 Deep Dive Workshop', eventDate:'Fri, Mar 20 2026', eventTime:'6:00 PM', hostName:'Amar Krishnan', platform:'Google Meet', meetLink:'https://meet.google.com/xyz-pqr-stu', dashboardUrl:'https://devbrains.in/dashboard/mentee', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.EVENT_REMINDER, category:'Events', enabled:true,
    label:'⏰ Event Reminder',
    to:'{{email}}',
    subject:'⏰ Starting tomorrow: {{eventTitle}} at {{eventTime}}',
    html: H('#8b5cf6','🎤','Event Tomorrow!') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8"><strong>{{eventTitle}}</strong> is happening <strong>tomorrow</strong>!</p>
<div style="background:#f5f3ff;border:2px solid #8b5cf6;border-radius:12px;padding:20px;text-align:center;margin:20px 0">
  <div style="font-size:18px;font-weight:800;color:#5b21b6">{{eventDate}}</div>
  <div style="font-size:28px;font-weight:900;color:#7c3aed;margin:4px 0">{{eventTime}} IST</div>
  <div style="color:#6d28d9;font-size:13px">Hosted by {{hostName}}</div>
</div>
<p style="text-align:center;margin:20px 0">${btn('{{meetLink}}','🎥 Join Event →','#8b5cf6')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','eventTitle','eventDate','eventTime','hostName','meetLink','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', eventTitle:'React 19 Deep Dive Workshop', eventDate:'Fri, Mar 20 2026', eventTime:'6:00 PM', hostName:'Amar Krishnan', meetLink:'https://meet.google.com/xyz-pqr-stu', platformUrl:'www.devbrains.in' },
  },

  // ── SUPPORT ──────────────────────────────────────────────────
  {
    id: EMAIL_EVENTS.SUPPORT_TICKET_CREATED, category:'Support', enabled:true,
    label:'🎫 Support Ticket Created',
    to:'{{email}}',
    subject:'🎫 Ticket #{{ticketId}} received — we\'ll respond within 24 hours',
    html: H('#2563eb','🎫','Support Ticket Received') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">We've received your support request and will respond within <strong>{{slaHours}} hours</strong>.</p>
${table(infoRow('🎫 Ticket ID','<code>#{{ticketId}}</code>')+infoRow('📋 Subject','{{ticketSubject}}')+infoRow('🔴 Priority','{{priority}}')+infoRow('📅 Opened','{{createdAt}}'))}
<p style="text-align:center;margin:20px 0">${btn('{{ticketUrl}}','View Ticket →','#2563eb')}</p>
<p style="color:#9ca3af;font-size:12px;text-align:center">Reply to this email to add more information to your ticket.</p>` +
F('{{platformUrl}}'),
    variables:['name','email','ticketId','ticketSubject','priority','slaHours','createdAt','ticketUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', ticketId:'TK-2026-0089', ticketSubject:'Payment deducted but booking not confirmed', priority:'High', slaHours:'24', createdAt:'Mar 10, 2026 at 11:42 AM', ticketUrl:'https://devbrains.in/support/TK-2026-0089', platformUrl:'www.devbrains.in' },
  },

  {
    id: EMAIL_EVENTS.SUPPORT_TICKET_RESOLVED, category:'Support', enabled:true,
    label:'✅ Support Ticket Resolved',
    to:'{{email}}',
    subject:'✅ Ticket #{{ticketId}} resolved — {{ticketSubject}}',
    html: H('#16a34a','✅','Ticket Resolved') +
`<p style="color:#374151">Hi <strong>{{name}}</strong>,</p>
<p style="color:#374151;line-height:1.8">Your support ticket <strong>#{{ticketId}}</strong> has been resolved by our team.</p>
<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px;margin:20px 0">
  <p style="margin:0 0 6px;font-weight:700;color:#166534;font-size:14px">📋 Resolution:</p>
  <p style="margin:0;color:#166534;font-size:13px">{{resolutionNote}}</p>
</div>
<p style="color:#374151;font-size:14px">If you're not satisfied, reply to this email and we'll reopen your ticket.</p>
<p style="text-align:center;margin:20px 0">${btn('{{ticketUrl}}','View Full Thread →','#16a34a')}</p>` +
F('{{platformUrl}}'),
    variables:['name','email','ticketId','ticketSubject','resolutionNote','ticketUrl','platformUrl'],
    preview:{ name:'Rahul', email:'rahul@ex.com', ticketId:'TK-2026-0089', ticketSubject:'Payment deducted but booking not confirmed', resolutionNote:'We found a processing delay and have manually confirmed your booking. You should now see the session in your dashboard. Apologies for the inconvenience.', ticketUrl:'https://devbrains.in/support/TK-2026-0089', platformUrl:'www.devbrains.in' },
  },
];

// ── Default SMTP config ────────────────────────────────────────
export const DEFAULT_EMAIL_CONFIG = {
  provider:       'smtp',
  smtpHost:       '',
  smtpPort:       587,
  smtpSecure:     false,
  smtpUser:       '',
  smtpPass:       '',
  fromEmail:      'noreply@devbrains.in',
  fromName:       'DevBrains',
  replyTo:        'support@devbrains.in',
  testEmail:      '',
  emailEnabled:   true,
  calendarInvites:true,
  unsubscribeLink:true,
};

const STORAGE_KEY = 'db_email_v2';

// ── Context ────────────────────────────────────────────────────
const EmailContext = createContext(null);

export function EmailProvider({ children }) {
  const saved = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; } })();

  const [config,    setConfig]    = useState({ ...DEFAULT_EMAIL_CONFIG, ...(saved?.config || {}) });
  const [templates, setTemplates] = useState(() => {
    if (!saved?.templates) return DEFAULT_TEMPLATES;
    return DEFAULT_TEMPLATES.map(t => {
      const s = saved.templates.find(x => x.id === t.id);
      return s ? { ...t, enabled: s.enabled, subject: s.subject ?? t.subject, html: s.html ?? t.html } : t;
    });
  });
  const [emailLog,  setEmailLog]  = useState([]);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        config,
        templates: templates.map(({ id, enabled, subject, html }) => ({ id, enabled, subject, html })),
      }));
    } catch {}
  }, [config, templates]);

  // ── Send email ─────────────────────────────────────────────
  const sendEmail = useCallback(async (eventId, vars = {}) => {
    const tmpl = templates.find(t => t.id === eventId);
    if (!tmpl || !tmpl.enabled || !config.emailEnabled) return false;

    const sub = str => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{${k}}}`, v ?? ''), str);

    const entry = {
      id: Date.now() + Math.random(),
      eventId, label: tmpl.label,
      to: sub(tmpl.to),
      subject: sub(tmpl.subject),
      status: 'pending',
      ts: new Date().toISOString(),
    };
    setEmailLog(prev => [entry, ...prev].slice(0, 100));

    const isMock  = process.env.REACT_APP_MOCK !== 'false';
    const apiUrl  = process.env.REACT_APP_API_URL;

    if (!isMock && apiUrl) {
      try {
        const token = sessionStorage.getItem('db_token') || localStorage.getItem('accessToken');
        const res = await fetch(`${apiUrl}/email/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            to:      entry.to,
            subject: entry.subject,
            html:    sub(tmpl.html),
            from:    `${config.fromName} <${config.fromEmail}>`,
            replyTo: config.replyTo,
          }),
        });
        const ok = res.ok;
        setEmailLog(prev => prev.map(l => l.id === entry.id ? { ...l, status: ok ? 'sent' : 'failed' } : l));
      } catch (err) {
        console.error('[DevBrains Email] send error:', err);
        setEmailLog(prev => prev.map(l => l.id === entry.id ? { ...l, status: 'failed' } : l));
      }
    } else {
      // Mock mode — simulate send
      console.log(`[DevBrains Email] ${tmpl.label} → ${entry.to}`);
      await new Promise(r => setTimeout(r, 150));
      setEmailLog(prev => prev.map(l => l.id === entry.id ? { ...l, status: 'sent' } : l));
    }
    return true;
  }, [templates, config]);

  const sendTestEmail = useCallback(async (templateId) => {
    const t = templates.find(x => x.id === templateId);
    if (!t) return false;
    return sendEmail(templateId, { ...t.preview, email: config.testEmail || config.fromEmail });
  }, [templates, config, sendEmail]);

  const toggleTemplate  = useCallback((id) => setTemplates(p => p.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t)), []);
  const updateTemplate  = useCallback((id, u) => setTemplates(p => p.map(t => t.id === id ? { ...t, ...u } : t)), []);
  const resetTemplate   = useCallback((id) => { const d = DEFAULT_TEMPLATES.find(t => t.id === id); if (d) setTemplates(p => p.map(t => t.id === id ? { ...d } : t)); }, []);
  const resetAllTemplates = useCallback(() => setTemplates(DEFAULT_TEMPLATES), []);

  // Add a brand-new custom template
  const addTemplate = useCallback((tmpl) => {
    const id = 'custom_' + Date.now();
    const newTmpl = {
      id,
      category: tmpl.category || 'Custom',
      label: tmpl.label || 'New Template',
      enabled: true,
      to: tmpl.to || '{{email}}',
      subject: tmpl.subject || '',
      html: tmpl.html || '',
      variables: tmpl.variables || [],
      preview: tmpl.preview || {},
      isCustom: true,
      createdAt: new Date().toISOString(),
    };
    setTemplates(p => [...p, newTmpl]);
    return id;
  }, []);

  // Delete a custom template (built-ins are protected)
  const deleteTemplate = useCallback((id) => {
    setTemplates(p => {
      const t = p.find(x => x.id === id);
      if (!t?.isCustom) return p; // protect built-in templates
      return p.filter(x => x.id !== id);
    });
  }, []);

  const updateConfig    = useCallback((u) => setConfig(p => ({ ...p, ...u })), []);
  const saveConfig      = useCallback(() => setLastSaved(new Date()), []);

  return (
    <EmailContext.Provider value={{
      config, updateConfig, saveConfig,
      templates, toggleTemplate, updateTemplate, resetTemplate, resetAllTemplates,
      addTemplate, deleteTemplate,
      sendEmail, sendTestEmail, emailLog, lastSaved,
    }}>
      {children}
    </EmailContext.Provider>
  );
}

export function useEmail() { return useContext(EmailContext); }
